/**
 * describePolyOrder — resolve a Polymarket asset_id (tokenId) into a
 * human-readable market description by scanning marketStore's currently-
 * loaded games. Returns null when no match is found — typically because
 * the relevant sport tab hasn't loaded yet, or the market expired before
 * the page mounted. Callers fall back to a truncated tokenId in that case.
 *
 * Why client-side: hydra's open-orders endpoint returns raw CLOB orders
 * with no market metadata attached — just asset_id, side, price, size.
 * The marketStore already has every loaded poly market's tokenId pair
 * (yesTokenId + noTokenId per team's VenueBook), so a sweep avoids a relay
 * round-trip per order.
 *
 * Two variants with one cache behind them:
 * - describePolyOrder (FRESH): sweeps every call. Order paths (edit/replace)
 *   use this — see its docstring for why stale tickSize/negRisk can destroy
 *   a resting order.
 * - describePolyOrderCached: render-path variant; resolved labels serve from
 *   cache, unresolved ids retry on a wall-clock cadence.
 */

import { useMarketStore } from '@/stores/marketStore';
import type { BookLevel } from '@/types';
import {
  SPORT_TABS,
  type MultiOutcomeEvent,
  type SeriesOutcome,
  type Sport,
  type VenueBook,
} from '@/views/ValueDashboard/types';

export interface PolyOrderDescription {
  /** "OKC @ SAS" — away vs home short codes. */
  matchup: string;
  /** Short code of the team this order's outcome resolves YES for. */
  betTeam: string;
  /** Whether the asset_id matches the team's YES or NO token. With the
   *  per-team Polymarket book convention (each team's row carries its own
   *  YES outcome + the cross-token NO outcome), `'yes'` means "{betTeam}
   *  wins" and `'no'` means "{betTeam} loses". */
  side: 'yes' | 'no';
  sport: Sport;
  /** YYYY-MM-DD as marketStore reports it on the GameRow. */
  date: string;
  /** Market-specific CLOB tick size. Required to safely replace fine-tick orders. */
  tickSize?: string;
  /** Market-specific negRisk flag. Required to safely replace NEG_RISK orders. */
  negRisk?: boolean;
  /** Slug ticker (PM-…) that keys marketStore for this market (Brendan,
   *  Jul 8). Immutable per token, so it's safe to cache — the LIVE data
   *  (last/volume/book) is read fresh by ticker via {@link polyOrderLiveData}
   *  rather than frozen here. Empty string when the slot has no ticker. */
  pmTicker: string;
}

function matchToken(assetId: string, book: VenueBook | null | undefined): 'yes' | 'no' | null {
  if (!book) return null;
  if (book.tokenId === assetId) return 'yes';
  if (book.noTokenId === assetId) return 'no';
  return null;
}

function describeMultiOutcomeBook(
  assetId: string,
  sport: Sport,
  event: MultiOutcomeEvent,
  outcome: SeriesOutcome,
): PolyOrderDescription | null {
  const book = outcome.polymarket;
  const side = matchToken(assetId, book);
  if (!side || !book) return null;
  const matchup =
    event.awayCode && event.homeCode
      ? `${event.awayCode} @ ${event.homeCode}`
      : event.title;
  const betTeam = outcome.role === 'draw' ? 'Draw' : outcome.name || outcome.code;
  return {
    matchup,
    betTeam,
    side,
    sport,
    date: event.date ?? '',
    tickSize: book.tickSize,
    negRisk: book.negRisk,
    pmTicker: book.marketTicker,
  };
}

/** How long the CACHED variant waits before re-sweeping for an id that
 *  didn't resolve (wall-clock, NOT store-version-denominated: a quiet feed
 *  stops bumping the version entirely, and an open order's label must never
 *  be hostage to feed activity). 3s keeps late-loading sports snappy while a
 *  dead/foreign assetId can't buy the full 14-sport scan on every render. */
const UNRESOLVED_RETRY_MS = 3_000;

interface DescCacheEntry {
  desc: PolyOrderDescription | null;
  at: number; // performance.now() of the sweep that produced this entry
}
const descCache = new Map<string, DescCacheEntry>();

export function _resetDescribePolyOrderCacheForTests(): void {
  descCache.clear();
}

/**
 * FRESH variant — always sweeps the store, then write-through-caches the
 * result (so the cached variant picks up resolutions immediately).
 *
 * ORDER PATHS MUST USE THIS ONE. tickSize/negRisk are NOT session-immutable
 * (hydra re-runs Gamma discovery every ~120s and can change a market's tick
 * regime), and the Polymarket replace flow is non-atomic cancel-then-place —
 * a stale tickSize/negRisk there gets the resting order cancelled and the
 * re-place rejected. A fresh sweep reads the store's ≤120s-fresh values.
 */
export function describePolyOrder(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const store = useMarketStore.getState();
  const desc = sweepForAssetId(assetId, store);
  descCache.set(assetId, { desc, at: performance.now() });
  return desc;
}

/**
 * CACHED variant — for the per-tick RENDER path only (row labels, card VMs,
 * sport grouping): a resolved entry serves forever (matchup/team/side/sport
 * are session-immutable; its tickSize/negRisk snapshot is display-grade —
 * anything order-shaped re-derives via the fresh variant at click time), and
 * an unresolved entry retries at most every UNRESOLVED_RETRY_MS. This sweep
 * used to run uncached per Poly order per tick from two call sites and was
 * the single worst frame spike on the positions page (~10ms per sport × 14
 * for an unresolvable id).
 */
export function describePolyOrderCached(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const cached = descCache.get(assetId);
  if (cached) {
    if (cached.desc !== null) return cached.desc;
    if (performance.now() - cached.at < UNRESOLVED_RETRY_MS) return null;
  }
  return describePolyOrder(assetId);
}

function sweepForAssetId(
  assetId: string,
  store: ReturnType<typeof useMarketStore.getState>,
): PolyOrderDescription | null {
  // Iterate every sport's moneyline games — the only market type whose
  // Polymarket book carries per-team token pairs today. Spread/total and
  // series rows route their token data differently and aren't covered by
  // the trader's current Polymarket order surface.
  //
  // statusFilter/dateFilter 'all': the defaults ('active'/'today') exclude
  // FINAL and non-today games, and an order/position label must never
  // depend on the game still being live — a settled position's row would
  // otherwise miss here every UNRESOLVED_RETRY_MS and re-run this
  // 14-sport sweep every 3s, forever (the exact frame-spike class the
  // cache exists to prevent).
  const sweepFilters = { statusFilter: 'all', dateFilter: 'all' } as const;
  for (const tab of SPORT_TABS) {
    const games = store.getGames(tab.id, 'moneyline', sweepFilters);
    for (const game of games) {
      const poly = game.polymarket;
      if (!poly) continue;
      for (const teamSide of ['away', 'home'] as const) {
        const book = poly[teamSide];
        const side = matchToken(assetId, book);
        if (!side || !book) continue;
        const betTeam = teamSide === 'away' ? game.awayCode : game.homeCode;
        return {
          matchup: `${game.awayCode} @ ${game.homeCode}`,
          betTeam,
          side,
          sport: tab.id,
          date: game.date,
          tickSize: book.tickSize,
          negRisk: book.negRisk,
          pmTicker: book.marketTicker,
        };
      }
    }
    for (const event of store.getMultiOutcomeEvents(tab.id, sweepFilters)) {
      for (const outcome of event.outcomes) {
        const desc = describeMultiOutcomeBook(assetId, tab.id, event, outcome);
        if (desc) return desc;
      }
    }
  }
  return null;
}

// ── Position → sellable token resolution ────────────────────────

export interface PolyCloseTarget {
  /** The outcome token the position actually holds — what a close SELLs. */
  tokenId: string;
  tickSize?: string;
  negRisk?: boolean;
}

/**
 * Resolve a held Polymarket position into its sellable outcome token.
 *
 * The relay's position ledger keys poly rows by the market CONDITION ID
 * (CLOB `Trade.market` — see relay/src/services/polyFillsPoller.ts), NOT
 * by tokenId; the side column says which of the market's two tokens the
 * fills bought. Placing any order requires the tokenId, so the pair is
 * mapped through the live store's token registry — the same rule
 * PositionTracker's U/P odds column uses.
 *
 * Returns null when the market isn't loaded in the store (sport tab never
 * opened, market expired). Callers MUST refuse to place and say why —
 * guessing a token or defaulting tickSize/negRisk on a live sell is how
 * money-path orders go wrong.
 */
export function resolvePolyCloseTarget(
  conditionId: string,
  side: 'yes' | 'no',
): PolyCloseTarget | null {
  if (!conditionId) return null;
  const store = useMarketStore.getState();
  // _markets is keyed by the PM- slug ticker, NOT the conditionId — the
  // direct getMarket(conditionId) this used to do was a guaranteed miss
  // (Cash Out shipped permanently disabled). Route through the store's
  // conditionId index; keep the direct read as a fallback in case a frame
  // ever keys by conditionId.
  const ticker = store.getTickerByConditionId(conditionId);
  const mkt = (ticker ? store.getMarket(ticker) : null) ?? store.getMarket(conditionId);
  if (!mkt) return null;
  const tokenId = side === 'yes' ? mkt.yesTokenId : mkt.noTokenId;
  if (!tokenId) return null;
  return { tokenId, tickSize: mkt.tickSize, negRisk: mkt.negRisk };
}

// ── Live per-order market data (Brendan, Jul 8) ─────────────────

export interface PolyOrderLiveData {
  /** Last trade price in cents for THIS order's outcome token (already
   *  inverted when the order holds the slug's NO token). null until a
   *  trade prints. */
  lastCents: number | null;
  /** Event start (ms) for the resting-order start label + start sort. */
  eventStartMs: number | null;
  /** Market traded volume. */
  volume: number | null;
  /** The order token's own bid ladder (best-bid first, cents) — feeds the
   *  queue / behind-by-N metrics. Empty when no book has streamed. */
  sideBids: BookLevel[];
}

const EMPTY_LIVE: PolyOrderLiveData = {
  lastCents: null,
  eventStartMs: null,
  volume: null,
  sideBids: [],
};

/**
 * Read the LIVE market data for a poly order, O(1) by its slug ticker —
 * no sport sweep. Separate from {@link describePolyOrder} because the
 * label resolution is cached forever (immutable) while this ticks with
 * the feed and must be read fresh every render.
 *
 * Orientation is derived from the raw slug market's own token ids
 * (yesTokenId === assetId), NOT from the away/home VenueBook split, so it
 * can't be fooled by the moneyline pair's ladder swap: the slug's `yes`
 * ladder is always the yesTokenId's bids.
 *
 * @param assetId  the order's outcome token (OpenOrder.asset_id)
 * @param pmTicker the slug ticker from the cached description
 */
export interface PolyPositionLiveData {
  /** Side-native mark for the HELD side: mid of the slug market's yes
   *  bid/ask (falling back to last trade), inverted for a NO position —
   *  the exact same recipe usePositionPnl uses for Kalshi rows, so the
   *  two venues' Now / P&L columns are comparable at a glance. */
  nowCents: number | null;
  /** Slug ticker the mark was read from; null when the market's frame
   *  hasn't been ingested (sport tab not loaded / market expired). */
  pmTicker: string | null;
}

const EMPTY_POSITION_LIVE: PolyPositionLiveData = { nowCents: null, pmTicker: null };

/**
 * Live side-native mark for a HELD poly position, keyed the way the
 * relay's position ledger keys rows: (conditionId, side). The ledger's
 * yes/no side is hydra's own orientation (yesTokenId/noTokenId — enforced
 * end-to-end by the relay's fills poller), and the slug frame's yes book
 * IS the yesTokenId's book, so `side === 'yes'` reads the yes mid
 * directly and NO inverts it.
 */
export function polyPositionLiveData(
  conditionId: string,
  side: 'yes' | 'no',
): PolyPositionLiveData {
  if (!conditionId) return EMPTY_POSITION_LIVE;
  const store = useMarketStore.getState();
  const ticker = store.getTickerByConditionId(conditionId);
  if (!ticker) return EMPTY_POSITION_LIVE;
  const yesMid = store.getMarketPrice(ticker);
  return {
    nowCents: yesMid == null ? null : side === 'yes' ? yesMid : 100 - yesMid,
    pmTicker: ticker,
  };
}

export function polyOrderLiveData(assetId: string, pmTicker: string): PolyOrderLiveData {
  if (!pmTicker) return EMPTY_LIVE;
  const store = useMarketStore.getState();
  const mkt = store.getMarket(pmTicker);
  if (!mkt) return EMPTY_LIVE;
  // Which of the slug's two tokens the order holds — orientation-proof.
  const slugSide: 'yes' | 'no' = mkt.yesTokenId === assetId ? 'yes' : 'no';
  const rawLastYes = store.getLastTradeYesCents(pmTicker);
  const lastCents =
    rawLastYes == null ? null : slugSide === 'yes' ? rawLastYes : 100 - rawLastYes;
  const raw = store.getOrderbookRaw(pmTicker);
  const rawBids = raw ? (slugSide === 'yes' ? raw.yes : raw.no) : [];
  const sideBids: BookLevel[] = rawBids.map((l) => ({ priceCents: l.price, size: l.size }));
  return {
    lastCents,
    eventStartMs: store.getEventStartTimeMsByTicker(pmTicker),
    volume: mkt.volume ?? null,
    sideBids,
  };
}
